// ── IDV Configuration Data ─────────────────────────────────────────────────
// Modelled on FrankieOne's Operations Center "IDV > Configuration" tab. The
// active flow and country/document restrictions materially affect the live
// Hosted OneSDK demo call; Vendor Settings, SMS delivery and audit-event
// forwarding are shown for parity with the real tab but are reference/local
// config only — there's no known live endpoint to push them to from here.
import type { IdvFlowConfig, IdvProvider, IdvSmsConfig, IdvVendorSettings } from './types'

export const IDV_PROVIDERS: IdvProvider[] = ['Daon', 'Incode', 'Onfido', 'Sumsub']

export const IDV_FLOWS: IdvFlowConfig[] = [
  { id: 'default', name: 'Default', type: '—', flowId: 'default' },
  { id: 'ekyc', name: 'eKYC', type: 'eKYC', flowId: 'manual_kyc' },
  { id: 'ekyc-bio', name: 'eKYC + Biometrics/OCR', type: 'Biometrics + OCR', flowId: 'idv_kyc' },
  { id: 'ocr-only', name: 'OCR only', type: '—', flowId: 'ocr_only' },
  { id: 'bio-ocr-incode', name: 'Biometrics/OCR Incode', type: 'Biometrics + OCR', flowId: 'idv_incode', provider: 'Incode' },
  { id: 'bio-ocr-onfido', name: 'Biometrics/OCR Onfido', type: 'Biometrics + OCR', flowId: 'idv_onfido', provider: 'Onfido' },
  { id: 'bio-ocr-sumsub', name: 'Biometrics/OCR Sumsub', type: 'Biometrics + OCR', flowId: 'idv_sumsub', provider: 'Sumsub' },
  { id: 'fraud-ekyc-bio', name: 'Fraud eKYC + Biometrics/OCR', type: '—', flowId: 'fraud_idv_kyc' },
  { id: 'fraud-ekyc', name: 'Fraud eKYC', type: '—', flowId: 'fraud_only_test' },
  { id: 'bio-ocr-daon', name: 'Biometrics/OCR (Daon)', type: 'Biometrics + OCR', flowId: 'idv_daon', provider: 'Daon' },
]

export const DOC_TYPES = ["Driver's Licence", 'Passport', 'National ID', 'National Health ID'] as const

export const IDV_COUNTRIES = [
  { code: 'AUS', label: 'AUS' },
  { code: 'NZL', label: 'NZL' },
  { code: 'GBR', label: 'GBR' },
  { code: 'USA', label: 'USA' },
  { code: 'SGP', label: 'SGP' },
  { code: 'CAN', label: 'CAN' },
] as const

export const DEFAULT_COUNTRY_RESTRICTIONS = [
  { countryCode: 'AUS', countryLabel: 'AUS', restrict: true, docTypes: ["Driver's Licence", 'Passport'] },
  { countryCode: 'NZL', countryLabel: 'NZL', restrict: true, docTypes: ["Driver's Licence", 'Passport'] },
]

// ── Vendor Settings — per-connector process definitions ──────────────────────
// Daon is shown pre-configured (a real customer's Daon connector always has
// named process definitions like these); the others default to Mock Mode on,
// since a demo tenant typically only has one vendor genuinely wired up.
export const DEFAULT_VENDOR_SETTINGS: Record<IdvProvider, IdvVendorSettings> = {
  Daon: {
    provider: 'Daon',
    mockMode: false,
    idvProcess: { processDefName: 'DevOnboardingFlow', processDefId: 'NL3YSVB6FWAC7Z5KBMJJZR3T3M', version: 12 },
    ocrProcess: { processDefName: 'UATDocCaptureDevWorkflow', processDefId: 'NKZYNSOP3VGEU5SNPFJSHJ6LGU', version: 1 },
    biometricsProcess: { processDefName: 'UATFaceCaptureWithExtraPortrait', processDefId: 'HQQTKFMPSPFBGUPW6OAPLT6A5A', version: 5 },
  },
  Incode: {
    provider: 'Incode',
    mockMode: true,
    idvProcess: { processDefName: '', processDefId: '', version: 1 },
    ocrProcess: { processDefName: '', processDefId: '', version: 1 },
    biometricsProcess: { processDefName: '', processDefId: '', version: 1 },
  },
  Onfido: {
    provider: 'Onfido',
    mockMode: true,
    idvProcess: { processDefName: '', processDefId: '', version: 1 },
    ocrProcess: { processDefName: '', processDefId: '', version: 1 },
    biometricsProcess: { processDefName: '', processDefId: '', version: 1 },
  },
  Sumsub: {
    provider: 'Sumsub',
    mockMode: true,
    idvProcess: { processDefName: '', processDefId: '', version: 1 },
    ocrProcess: { processDefName: '', processDefId: '', version: 1 },
    biometricsProcess: { processDefName: '', processDefId: '', version: 1 },
  },
}

// ── SMS & Send Link — verification-link delivery ──────────────────────────────
export const DEFAULT_SMS_CONFIG: IdvSmsConfig = {
  sendSms: true,
  senderId: 'Frankie',
  sessionExpiryHours: 24,
  verificationDomain: 'https://verify.uat.frankie.one/v2/',
  messageTemplate: 'Hi {applicantName}, to complete your registration we just need you to head to our partner FrankieOne to verify your identity. {verificationLink} You must complete this within the next {sessionExpiryHours} hours to finish the verification process.',
}

// ── Telemetry & Audit Events — which OneSDK events get forwarded to the audit log ─
export const DEFAULT_AUDIT_EVENTS = ['INIT', '*:MOUNT', '*:RESULTS', 'SUBMIT*', '*:LOADED', '*:ERROR', '*:READY']
